import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Shield,
  Snowflake,
  Smartphone,
  MapPin,
  Tag,
  ArrowLeft,
  CheckCircle2,
  ShoppingCart,
  Check,
  ZoomIn,
  Truck,
  Clock,
  Zap,
  Package,
  CreditCard,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import Navbar from "../components/nav/Navbar";
import ProductGridCard from "../components/ProductGridCard";
import { addItem, selectCartItems } from "../store/cartSlice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CTA_H      = 54;
const CTA_RADIUS = 14;
const CTA_SHADOW = "0 2px 8px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)";
const CTA_GAP    = 10;

function QuoteDialog({ productName, formData, onChange, onSelect, onSubmit, submitting }) {
  return (
    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading font-semibold text-2xl text-foreground text-center">
          Get Business Pricing{productName ? ` — ${productName}` : ""}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-6 mt-4">
        {[
          ["Contact Information", [
            { id: "customer_name", label: "Full Name *",    type: "text",  required: true },
            { id: "email",         label: "Email *",        type: "email", required: true },
            { id: "phone",         label: "Phone *",        type: "tel",   required: true },
            { id: "company_name",  label: "Company Name",   type: "text" },
          ]],
        ].map(([section, fields]) => (
          <div key={section} className="space-y-4">
            <h3 className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              {section}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.id}>
                  <Label htmlFor={f.id} className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {f.label}
                  </Label>
                  <Input
                    id={f.id} name={f.id} type={f.type}
                    value={formData[f.id]} onChange={onChange}
                    required={f.required}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="quantity" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Quantity *</Label>
            <Input id="quantity" name="quantity" type="number" min="1" value={formData.quantity} onChange={onChange} required className="mt-1" />
          </div>
          <div>
            <Label htmlFor="location" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Delivery Location *</Label>
            <Input id="location" name="location" placeholder="City, State, Country" value={formData.location} onChange={onChange} required className="mt-1" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Budget Range</Label>
            <Select value={formData.budget_range} onValueChange={(v) => onSelect("budget_range", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="under_3000">Under $3,000</SelectItem>
                <SelectItem value="3000_5000">$3,000 – $5,000</SelectItem>
                <SelectItem value="5000_10000">$5,000 – $10,000</SelectItem>
                <SelectItem value="10000_plus">$10,000+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="customization_details" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Notes / Customization</Label>
          <Textarea id="customization_details" name="customization_details" placeholder="Branding requirements, colors, special needs..." value={formData.customization_details} onChange={onChange} className="mt-1 min-h-[90px]" />
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
          {submitting ? "Submitting..." : "Submit Quote Request"}
        </button>
      </form>
    </DialogContent>
  );
}

function PhotoGallery({ images, productName }) {
  const [idx, setIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const all = images?.length ? images : [null];
  const total = all.length;
  useEffect(() => { setIdx(0); }, [images]);
  const safeIdx = Math.min(idx, Math.max(0, total - 1));

  useEffect(() => {
    if (!zoomed) return;
    const handler = (e) => { if (e.key === 'Escape') setZoomed(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomed]);

  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative overflow-hidden flex items-center justify-center"
        style={{ aspectRatio: "4/3", background: "rgba(255,255,255,0.03)", borderRadius: 16, border: "1px solid rgba(255,255,255,0.08)", cursor: all[safeIdx] ? "zoom-in" : "default" }}
        onClick={() => { if (all[safeIdx]) setZoomed(true); }}
      >
        {all[safeIdx] ? (
          <img src={all[safeIdx]} alt={`${productName} view ${safeIdx + 1}`} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <div className="flex flex-col items-center gap-3 text-muted-foreground/25">
            <ImageOff className="h-16 w-16" />
            <span className="font-mono text-[10px] uppercase tracking-widest">Images Coming Soon</span>
          </div>
        )}
        {total > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + total) % total); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full glass flex items-center justify-center transition-all hover:scale-110"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % total); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full glass flex items-center justify-center transition-all hover:scale-110"><ChevronRight className="h-4 w-4" /></button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {all.map((_, i) => (<button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} style={{ width: i === safeIdx ? 20 : 7, height: 7, borderRadius: 9999, background: i === safeIdx ? "var(--vb-blue)" : "rgba(255,255,255,0.2)", transition: "all 0.2s ease" }} />))}
            </div>
          </>
        )}
        {all[safeIdx] && (
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", fontSize: 10, color: "rgba(255,255,255,0.7)", fontFamily: "'Inter', sans-serif" }}>
            <ZoomIn size={11} /> Click to zoom
          </div>
        )}
      </div>
      {total > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {all.map((img, i) => (<button key={i} onClick={() => setIdx(i)} className="flex-shrink-0 overflow-hidden transition-all" style={{ width: 72, height: 56, borderRadius: 10, border: `2px solid ${i === idx ? "var(--vb-blue)" : "rgba(255,255,255,0.1)"}`, opacity: i === idx ? 1 : 0.55 }}>{img ? <img src={img} alt={`thumb ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", background: "rgba(255,255,255,0.04)" }} />}</button>))}
        </div>
      )}
      {zoomed && all[safeIdx] && (
        <div
          onClick={() => setZoomed(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out", animation: "fadeIn 0.2s ease" }}
        >
          <img src={all[safeIdx]} alt={`${productName} zoom`} style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }} />
          {total > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + total) % total); }} style={{ position: "absolute", left: 24, top: "50%", transform: "translateY(-50%)", width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={24} /></button>
              <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % total); }} style={{ position: "absolute", right: 24, top: "50%", transform: "translateY(-50%)", width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronRight size={24} /></button>
            </>
          )}
          <button onClick={() => setZoomed(false)} style={{ position: "absolute", top: 24, right: 24, width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>✕</button>
          <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
            {safeIdx + 1} / {total}
          </div>
        </div>
      )}
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
}

function SpecRow({ label, value }) {
  if (!value || value === "None") return null;
  return (<div className="spec-row"><span className="spec-label">{label}</span><span className="spec-value">{value}</span></div>);
}

function FeatureChip({ icon, label }) {
  return (<div className="chip">{icon}<span>{label}</span></div>);
}

// ── Shipping Options ──────────────────────────────────────────────────────────
function ShippingOptions({ product, selected, onSelect }) {
  const addDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const freeDate    = addDays(45);
  const expressDate = addDays(25);
  const hasExpress  = product?.shipping_express_cost > 0;
  const hasSameDay  = product?.shipping_same_day_cost > 0;

  const options = [
    {
      key: 'free',
      icon: <Truck size={20} strokeWidth={1.6} />,
      label: 'Free Shipping',
      sub: `Get it by ${freeDate}`,
      selectable: true,
      cost: 0,
    },
    {
      key: 'sameday',
      icon: <Clock size={20} strokeWidth={1.6} />,
      label: 'Same-Day',
      sub: hasSameDay ? `+${product.shipping_same_day_cost?.toLocaleString()}` : 'Not available',
      selectable: hasSameDay,
      cost: product?.shipping_same_day_cost || 0,
    },
    {
      key: 'express',
      icon: <Zap size={20} strokeWidth={1.6} />,
      label: 'Express',
      sub: hasExpress ? `Get it by ${expressDate} · +${product.shipping_express_cost?.toLocaleString()}` : 'Not available',
      selectable: hasExpress,
      cost: product?.shipping_express_cost || 0,
    },
  ];

  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
        Shipping
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        {options.map((opt) => {
          const isSelected = selected === opt.key;
          const isDisabled = !opt.selectable;
          return (
            <button
              key={opt.key}
              onClick={() => { if (opt.selectable) onSelect(opt.key, opt.cost); }}
              disabled={isDisabled}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 6,
                padding: '12px 14px',
                borderRadius: 12,
                border: `1.5px solid ${
                  isSelected
                    ? 'var(--vb-blue)'
                    : isDisabled
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.12)'
                }`,
                background: isSelected
                  ? 'rgba(91,140,255,0.08)'
                  : isDisabled
                  ? 'rgba(255,255,255,0.02)'
                  : 'rgba(255,255,255,0.04)',
                color: isDisabled ? 'rgba(255,255,255,0.3)' : 'hsl(var(--foreground))',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                textAlign: 'left',
              }}
            >
              <span style={{
                color: isSelected
                  ? 'var(--vb-blue)'
                  : isDisabled
                  ? 'rgba(255,255,255,0.2)'
                  : 'rgba(255,255,255,0.65)',
              }}>
                {opt.icon}
              </span>
              <div>
                <p style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: 12,
                  lineHeight: 1.2,
                  color: isDisabled
                    ? 'rgba(255,255,255,0.3)'
                    : 'hsl(var(--foreground))',
                }}>
                  {opt.label}
                </p>
                <p style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11,
                  marginTop: 2,
                  color: isSelected
                    ? 'var(--vb-blue)'
                    : isDisabled
                    ? 'rgba(255,255,255,0.2)'
                    : 'rgba(255,255,255,0.45)',
                }}>
                  {opt.sub}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AddToCartButtonFull({ product, selectedColor, shippingOption, shippingCost }) {
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const [flash, setFlash] = useState(false);
  const pid = product?.mongo_id || product?.id;
  const cartQty = cartItems.filter((i) => (i.product.mongo_id || i.product.id) === pid).reduce((s, i) => s + i.quantity, 0);
  const inCart = cartQty > 0;
  const handleAdd = () => { dispatch(addItem(product, selectedColor, shippingOption || 'free', shippingCost || 0)); setFlash(true); toast.success(`${product.product_name || "Item"}${selectedColor ? ` (${selectedColor})` : ""} added to cart`, { duration: 2000, position: "bottom-right" }); setTimeout(() => setFlash(false), 1400); };
  const isFlashing = flash;

  // Base: gradient primary style (same as former BuyNowButton)
  // Flash: green confirmation. inCart: indigo accent to signal item already added.
  const bg     = isFlashing ? "#16a34a" : inCart ? "var(--vb-indigo)" : "var(--grad-primary)";
  const shadow = isFlashing ? "0 4px 14px rgba(22,163,74,0.30)" : CTA_SHADOW;
  const iconEl = isFlashing ? <Check size={16} strokeWidth={2.5} /> : <ShoppingCart size={16} strokeWidth={1.8} />;
  const label  = isFlashing ? "Added to Cart" : inCart ? `${cartQty} in Cart — Add Another` : "Add to Cart";

  return (
    <button
      onClick={handleAdd}
      style={{
        height: CTA_H, width: "100%", borderRadius: CTA_RADIUS,
        border: "none", padding: "0 28px",
        background: bg,
        color: "#fff",
        fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15,
        cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        boxShadow: shadow,
        transition: "all 0.15s ease-out",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.filter = "brightness(1.07)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.filter = ""; }}>
      {iconEl}{label}
    </button>
  );
}

function QuoteButton({ onClick }) {
  return (
    <button onClick={onClick} style={{ height: CTA_H, width: "100%", borderRadius: CTA_RADIUS, border: "1.5px solid hsl(var(--primary))", padding: "0 28px", background: "transparent", color: "hsl(var(--primary))", fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, letterSpacing: "0.02em", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: CTA_SHADOW, transition: "all 0.15s ease-out" }}
      onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--secondary))"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(106,0,244,0.13)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = CTA_SHADOW; }}>
      Get a Custom Quote →
    </button>
  );
}

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState('free');
  const [selectedShippingCost, setSelectedShippingCost] = useState(0);

  // Default to first available color once product data loads
  useEffect(() => {
    if (product?.available_colors?.length > 0) {
      setSelectedColor(product.available_colors[0]);
    }
  }, [product]);
  const [formData, setFormData] = useState({ customer_name: "", email: "", phone: "", company_name: "", location: "", quantity: 1, customization_details: "", budget_range: "", payment_options: [], additional_features: [], machine_type: "" });

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); setProduct(null); setLoading(true); setRelated([]); }, [productId]);
  useEffect(() => { const fetch = async () => { try { const res = await axios.get(`${API}/products/${productId}`); setProduct(res.data); } catch {} finally { setLoading(false); } }; fetch(); }, [productId]);
  useEffect(() => { if (!product) return; axios.get(`${API}/products`, { params: { status: "Active" } }).then((r) => { const all = Array.isArray(r.data) ? r.data : []; const current = product.mongo_id || product.id; const same = all.filter(p => p.machine_type === product.machine_type && (p.mongo_id || p.id) !== current); const others = all.filter(p => p.machine_type !== product.machine_type && (p.mongo_id || p.id) !== current); setRelated([...same, ...others].slice(0, 6)); }).catch(() => {}); }, [product]);
  useEffect(() => { if (!loading && !product) { const t = setTimeout(() => navigate("/shop"), 2500); return () => clearTimeout(t); } }, [loading, product, navigate]);

  const onChange = (e) => { const { name, value } = e.target; setFormData((prev) => ({ ...prev, [name]: value })); };
  const onSelect = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));
  const handleQuoteSubmit = async (e) => { e.preventDefault(); setIsSubmitting(true); try { const res = await axios.post(`${API}/quotes`, { ...formData, machine_type: product?.machine_type || formData.machine_type, customization_details: `Product: ${product?.product_name}\n\n${formData.customization_details}` }); if (res.data.success) { toast.success("Quote submitted! We'll be in touch within 24 hours."); setIsQuoteOpen(false); } } catch { toast.error("Failed to submit. Please try again."); } finally { setIsSubmitting(false); } };

  const hasDiscount = product?.original_price && product?.sale_price && product.sale_price < product.original_price;
  const discountPct = hasDiscount ? Math.round(((product.original_price - product.sale_price) / product.original_price) * 100) : null;
  const fmtPrice = (v) => v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) { return (<div className="min-h-screen bg-background"><Navbar /><div className="container-lg pb-20" style={{ paddingTop: "calc(var(--header-h) + 52px)" }}><div className="grid lg:grid-cols-2 gap-16 animate-pulse"><div className="skeleton" style={{ aspectRatio: "4/3", borderRadius: 20 }} /><div className="flex flex-col gap-4"><div className="skeleton h-4 w-1/3" /><div className="skeleton h-10 w-3/4" /><div className="skeleton h-4 w-1/2" /><div className="skeleton h-28 w-full" /><div className="skeleton h-14 w-full" /></div></div></div></div>); }

  if (!product) { return (<div className="min-h-screen bg-background flex items-center justify-center"><Navbar /><div className="text-center px-6"><p className="font-heading text-2xl text-foreground mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Product not found</p><p className="font-body text-sm text-muted-foreground mb-6">Redirecting to the full catalogue&hellip;</p><button onClick={() => navigate("/shop")} className="btn-secondary"><ArrowLeft className="h-4 w-4" /> Browse All Machines</button></div></div>); }

  const galleryImages = product.photo_urls?.length ? product.photo_urls : product.img_url ? [product.img_url] : [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        onCtaClick={() => setIsQuoteOpen(true)}
        ctaLabel="Get Business Pricing"
      />
      <div
        className="container-lg pb-2"
        style={{ paddingTop: "calc(var(--header-h) + 52px)" }}>
        <button
          onClick={() => navigate("/shop")}
          className="flex items-center gap-1.5 font-body text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to catalogue
        </button>
      </div>
      <div className="container-lg py-10 lg:py-14">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start">
          <div
            className="lg:sticky"
            style={{ top: "calc(var(--header-h) + 52px)" }}>
            <PhotoGallery
              images={galleryImages}
              productName={product.product_name}
            />
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2 flex-wrap">
              {product.machine_type && (
                <span className="chip chip-primary">
                  <Tag className="h-3 w-3" />
                  {product.machine_type}
                </span>
              )}
              {product.brand_name && (
                <span className="font-body text-xs text-muted-foreground">
                  by {product.brand_name}
                </span>
              )}
            </div>
            <h1
              className="font-heading text-4xl lg:text-5xl text-foreground leading-tight"
              style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600 }}>
              {product.product_name}
            </h1>
            {product.advantage && (
              <p className="font-body text-base text-muted-foreground leading-relaxed">
                {product.advantage}
              </p>
            )}
            <div
              style={{
                background: "rgba(255,255,255,0.04)",
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,0.08)",
                padding: "20px 24px",
              }}>
              {hasDiscount ? (
                <div className="flex items-end gap-4 flex-wrap">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Sale Price
                    </p>
                    <span
                      className="font-heading text-4xl font-bold"
                      style={{
                        color: "var(--vb-blue)",
                        fontFamily: "'Oswald', sans-serif",
                      }}>
                      ${fmtPrice(product.sale_price)}
                    </span>
                  </div>
                  <div className="pb-1 flex items-center gap-2">
                    <span
                      className="font-body text-lg line-through"
                      style={{ color: "rgba(255,255,255,0.5)" }}>
                      ${fmtPrice(product.original_price)}
                    </span>
                    <span className="chip chip-discount">
                      {discountPct}% OFF
                    </span>
                  </div>
                </div>
              ) : product.sale_price ? (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    Price
                  </p>
                  <span
                    className="font-heading text-4xl font-bold"
                    style={{
                      color: "var(--vb-blue)",
                      fontFamily: "'Oswald', sans-serif",
                    }}>
                    ${fmtPrice(product.sale_price)}
                  </span>
                </div>
              ) : (
                <p className="font-body text-base font-semibold text-foreground">
                  Custom pricing — contact us for a quote
                </p>
              )}
              {hasDiscount && (
                <span className="chip chip-savings mt-3 inline-flex">
                  You save $
                  {fmtPrice(product.original_price - product.sale_price)}
                </span>
              )}
              {product.sale_price && (
                <p
                  className="font-body text-xs mt-3"
                  style={{ color: "var(--vb-blue)", opacity: 0.85 }}>
                  Estimated ROI: 6–12 months · Avg. monthly revenue: $500–$2,000
                </p>
              )}
              {product.moq && (
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Min. order: {product.moq}
                </p>
              )}
            </div>

            {/* Color selector */}
            {product.available_colors?.length > 0 && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  Select Color
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.available_colors.map((color) => {
                    const isSelected = selectedColor === color;
                    return (
                      <button
                        key={color}
                        onClick={() =>
                          setSelectedColor(isSelected ? null : color)
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 14px",
                          borderRadius: 10,
                          border: `1.5px solid ${isSelected ? "var(--vb-blue)" : "rgba(255,255,255,0.12)"}`,
                          background: isSelected
                            ? "rgba(91,140,255,0.1)"
                            : "rgba(255,255,255,0.04)",
                          color: isSelected
                            ? "var(--vb-blue)"
                            : "hsl(var(--foreground))",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}>
                        {isSelected && <Check size={13} strokeWidth={2.5} />}
                        {color}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shipping options */}
            <ShippingOptions
              product={product}
              selected={selectedShipping}
              onSelect={(key, cost) => { setSelectedShipping(key); setSelectedShippingCost(cost); }}
            />

            {/* CTAs */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: CTA_GAP,
              }}>
              {product.sale_price && (
                <AddToCartButtonFull
                  product={product}
                  selectedColor={selectedColor}
                  shippingOption={selectedShipping}
                  shippingCost={selectedShippingCost}
                />
              )}
              <Dialog open={isQuoteOpen} onOpenChange={setIsQuoteOpen}>
                <DialogTrigger asChild>
                  <QuoteButton onClick={() => setIsQuoteOpen(true)} />
                </DialogTrigger>
                <QuoteDialog
                  productName={product.product_name}
                  formData={formData}
                  onChange={onChange}
                  onSelect={onSelect}
                  onSubmit={handleQuoteSubmit}
                  submitting={isSubmitting}
                />
              </Dialog>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {[
                "Visa",
                "Mastercard",
                "Amex",
                "Discover",
                "Apple Pay",
                "Google Pay",
              ].map((n) => (
                <span key={n} className="payment-badge">
                  {n}
                </span>
              ))}
            </div>

            {/* Trust module */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "14px 16px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}>
              {[
                { icon: "✔", text: "Verified Supplier" },
                { icon: "✔", text: "1-Year Warranty Included" },
                { icon: "✔", text: "Secure Stripe Checkout" },
                { icon: "✔", text: "Ships in ~21 days after production" },
              ].map(({ icon, text }) => (
                <div
                  key={text}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      fontSize: 12,
                      flexShrink: 0,
                      width: 16,
                      textAlign: "center",
                    }}>
                    {icon}
                  </span>
                  <span
                    className="font-body text-xs"
                    style={{ color: "rgba(255,255,255,0.7)" }}>
                    {text}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {product.touch_screen === "Yes" && (
                <FeatureChip
                  icon={<Smartphone className="h-3 w-3" />}
                  label="Touch Screen"
                />
              )}
              {product.cooling_system && product.cooling_system !== "None" && (
                <FeatureChip
                  icon={<Snowflake className="h-3 w-3" />}
                  label="Refrigerated"
                />
              )}
              {product.payment_methods?.includes("Credit Card") && (
                <FeatureChip
                  icon={<CreditCard className="h-3 w-3" />}
                  label="Cashless Ready"
                />
              )}
              {product.warranty && (
                <FeatureChip
                  icon={<Shield className="h-3 w-3" />}
                  label={product.warranty}
                />
              )}
              {product.suitable_locations?.slice(0, 2).map((loc) => (
                <FeatureChip
                  key={loc}
                  icon={<MapPin className="h-3 w-3" />}
                  label={loc}
                />
              ))}
            </div>

            {product.great_points?.length > 0 && (
              <div className="flex flex-col gap-3 pt-2">
                {product.great_points.map((pt, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2
                      className="h-5 w-5 flex-shrink-0 mt-0.5"
                      style={{ color: "var(--vb-blue)" }}
                    />
                    <p className="font-body text-sm text-foreground leading-relaxed">
                      {pt}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {product.description_a && (
              <p className="font-body text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">
                {product.description_a}
              </p>
            )}
            {product.description_b && (
              <p className="font-body text-sm text-muted-foreground leading-relaxed italic">
                {product.description_b}
              </p>
            )}

            <div
              style={{
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 16,
                overflow: "hidden",
              }}>
              <div
                style={{
                  padding: "12px 20px",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.04)",
                }}>
                <p className="font-mono text-[11px] uppercase tracking-widest text-foreground font-semibold">
                  Full Specifications
                </p>
              </div>
              <div style={{ padding: "0 20px" }}>
                <SpecRow label="Capacity" value={product.capacity} />
                <SpecRow label="Dimensions" value={product.dimensions} />
                <SpecRow label="Weight" value={product.weight} />
                <SpecRow label="Voltage" value={product.voltage} />
                <SpecRow label="Power" value={product.power} />
                <SpecRow
                  label="Cooling System"
                  value={product.cooling_system}
                />
                <SpecRow label="Display" value={product.display_type} />
                <SpecRow label="Touch Screen" value={product.touch_screen} />
                <SpecRow
                  label="Payment Methods"
                  value={product.payment_methods?.join(", ")}
                />
                <SpecRow
                  label="Certifications"
                  value={product.certifications?.join(", ")}
                />
                <SpecRow label="Origin" value={product.place_of_origin} />
                <SpecRow label="MOQ" value={product.moq} />
                <SpecRow label="Warranty" value={product.warranty} />
              </div>
            </div>

            {product.suitable_locations?.length > 0 && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                  Ideal Locations
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.suitable_locations.map((loc) => (
                    <span key={loc} className="chip">
                      {loc}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {product.description_c && (
              <p className="font-body text-xs text-muted-foreground leading-relaxed border-t border-border pt-4">
                {product.description_c}
              </p>
            )}

            {product.sale_price && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: CTA_GAP,
                  paddingTop: 8,
                }}>
                <AddToCartButtonFull
                  product={product}
                  selectedColor={selectedColor}
                  shippingOption={selectedShipping}
                  shippingCost={selectedShippingCost}
                />
                <p className="font-body text-xs text-muted-foreground text-center">
                  Secure Stripe checkout · ~21 days transit after production
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section
          style={{
            background: "rgba(255,255,255,0.02)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: "64px 0 80px",
          }}>
          <div className="container-lg">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                  You Might Also Like
                </p>
                <h2
                  className="font-heading text-2xl text-foreground"
                  style={{
                    fontFamily: "'Oswald', sans-serif",
                    fontWeight: 600,
                  }}>
                  More Machines
                </h2>
              </div>
              <button onClick={() => navigate("/shop")} className="btn-ghost">
                View All →
              </button>
            </div>
            <div
              className="swipe-row md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
              style={{ gap: 16 }}>
              {related.map((p) => (
                <ProductGridCard
                  key={p.mongo_id || p.id}
                  product={p}
                  mobile={true}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <footer
        style={{
          background: "#050507",
          color: "#f0f0f5",
          padding: "48px 0",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}>
        <div className="container-lg flex items-center justify-between flex-wrap gap-6">
          <div>
            <p
              className="font-heading text-base font-semibold mb-1"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Vendbabee
            </p>
            <p
              className="font-body text-xs"
              style={{ color: "rgba(248,247,255,0.4)" }}>
              Smart vending machines for the US, Canada, EU, and beyond.
            </p>
          </div>
          <p
            className="font-body text-xs"
            style={{ color: "rgba(248,247,255,0.25)" }}>
            © {new Date().getFullYear()} Vendbabee. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
